// src/components/GlobalSearchPalette.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { Search, Users as UsersIcon, BookOpen } from "lucide-react";
import { HELP_TOPICS, HELP_PLAN_TOPIC_ACCESS } from "../pages/Help";

import "../styles/AdminDashboard.css"; // uses same design tokens

const normalize = (str = "") => str.toLowerCase().trim();

/**
 * props:
 * - open: boolean
 * - onClose: () => void
 * - navItems: [{ id, label, description, type: 'tab'|'route', tab?, path? }]
 * - planName: string (Basic / Standard / Premium / Training / Helpdesk)
 * - onTabChange: (tabKey: string) => void
 */
export default function GlobalSearchPalette({
  open,
  onClose,
  navItems,
  planName = "Basic",
  onTabChange,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [q, setQ] = useState("");
  const [employees, setEmployees] = useState([]);
  const [empLoaded, setEmpLoaded] = useState(false);
  const [empLoading, setEmpLoading] = useState(false);

  // Simple keyboard selection (flat list)
  const [flatResults, setFlatResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Load employees once (when palette first opens)
  useEffect(() => {
    if (!open || empLoaded || !user?.companyId) return;

    const fetchEmployees = async () => {
      try {
        setEmpLoading(true);
        const colRef = collection(db, `companies/${user.companyId}/employees`);
        const q = query(colRef, orderBy("name", "asc"));
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setEmployees(rows);
        setEmpLoaded(true);
      } catch (e) {
        console.error("GlobalSearch: failed to load employees", e);
      } finally {
        setEmpLoading(false);
      }
    };

    fetchEmployees();
  }, [open, empLoaded, user?.companyId]);

  // Reset search when opening
  useEffect(() => {
    if (open) {
      setQ("");
      setSelectedIndex(0);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const allowedHelpIds = useMemo(() => {
    const map =
      HELP_PLAN_TOPIC_ACCESS[planName] || HELP_PLAN_TOPIC_ACCESS.Basic;
    return map;
  }, [planName]);

  const visibleHelpTopics = useMemo(
    () => HELP_TOPICS.filter((t) => allowedHelpIds.has(t.id)),
    [allowedHelpIds]
  );

  const term = normalize(q);

  // --- Employee results ---
  const employeeResults = useMemo(() => {
    if (!term) return [];
    const t = term;

    return employees
      .filter((e) => {
        const fullName = `${e.name || ""} ${e.middleName || ""} ${e.lastName ||
          ""}`;
        const haystack = normalize(
          [fullName, e.email, e.jobTitle, e.group, e.role, e.status]
            .filter(Boolean)
            .join(" ")
        );
        return haystack.includes(t);
      })
      .slice(0, 6);
  }, [employees, term]);

  // --- Nav page results ---
  const navResults = useMemo(() => {
    const base = navItems || [];
    if (!term) return base.slice(0, 6);

    const t = term;
    return base
      .filter((item) => {
        const haystack = normalize(
          `${item.label} ${item.description || ""} ${item.keywords || ""}`
        );
        return haystack.includes(t);
      })
      .slice(0, 6);
  }, [navItems, term]);

  // --- Help topic results ---
  const helpResults = useMemo(() => {
    if (!term) return [];

    const t = term;
    return visibleHelpTopics
      .filter((topic) => {
        const haystack = normalize(
          [topic.title, topic.summary, ...(topic.tags || [])].join(" ")
        );
        return haystack.includes(t);
      })
      .slice(0, 6);
  }, [visibleHelpTopics, term]);

  // Build a flat list for arrow-key navigation
  useEffect(() => {
    const flat = [];

    if (employeeResults.length) {
      employeeResults.forEach((e) => flat.push({ kind: "employee", data: e }));
    }
    if (navResults.length) {
      navResults.forEach((n) => flat.push({ kind: "nav", data: n }));
    }
    if (helpResults.length) {
      helpResults.forEach((h) => flat.push({ kind: "help", data: h }));
    }

    setFlatResults(flat);
    setSelectedIndex(0);
  }, [employeeResults, navResults, helpResults]);

  // Arrow-key navigation + Enter
  useEffect(() => {
    if (!open) return;
    if (!flatResults.length) return;

    const handler = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flatResults[selectedIndex];
        if (item) {
          handleSelect(item);
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flatResults, selectedIndex]);

  const handleSelect = useCallback(
    (item) => {
      if (!item) return;

      const { kind, data } = item;

      if (kind === "employee") {
        // Go straight to the standalone Employee Details page
        navigate(`/employee/${data.id}`, {
          state: { fromGlobalSearch: true },
        });
        onClose();
        return;
      }

      if (kind === "nav") {
        if (data.type === "tab" && onTabChange) {
          onTabChange(data.tab);
        } else if (data.type === "route" && data.path) {
          navigate(data.path, {
            state: data.navState || {},
          });
        }
        onClose();
        return;
      }

      // 🔧 FIX: help items should *always* go to the Help Center tab
      if (kind === "help") {
        if (onTabChange) {
          onTabChange("help"); // force the Help Center tab
        }
        // (optional: if you ever want to pass which article to highlight,
        // you can store data.id in some global state or context here)
        onClose();
        return;
      }
    },
    [navigate, onClose, onTabChange]
  );

  if (!open) return null;

  return (
    <div
      className="gs-backdrop"
      aria-modal="true"
      role="dialog"
      onClick={(e) => {
        // Only close if the click was on the backdrop itself,
        // not inside the dialog.
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="gs-dialog" role="document">
        <header className="gs-header">
          <div className="gs-input-wrap">
            <Search className="gs-input-icon" />
            <input
              autoFocus
              className="gs-input"
              placeholder="Search employees, pages, help…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="gs-kbd-hint">
              <span className="gs-kbd">Esc</span>
              <span className="gs-kbd">Enter</span>
            </div>
          </div>
          <div className="gs-subhint">
            {empLoading
              ? "Loading employees…"
              : "Tip: Press ⌘K / Ctrl+K from anywhere in the dashboard."}
          </div>
        </header>

        <div className="gs-body">
          {!flatResults.length && !empLoading ? (
            <div className="gs-empty">
              {term
                ? "No results. Try a different name, page, or keyword."
                : "Start typing to search across employees, pages, and help topics."}
            </div>
          ) : (
            <>
              {employeeResults.length > 0 && (
                <section className="gs-section">
                  <div className="gs-section-title">
                    <UsersIcon className="gs-section-icon" />
                    <span>Employees</span>
                  </div>
                  <ul className="gs-list">
                    {employeeResults.map((e, idx) => {
                      const fullName = [e.name, e.middleName, e.lastName]
                        .filter(Boolean)
                        .join(" ");
                      const selected =
                        flatResults[selectedIndex]?.kind === "employee" &&
                        flatResults[selectedIndex]?.data.id === e.id;
                      return (
                        <li key={e.id}>
                          <button
                            type="button"
                            className={`gs-item ${selected ? "selected" : ""}`}
                            onClick={() =>
                              handleSelect({
                                kind: "employee",
                                data: e,
                              })
                            }
                          >
                            <div className="gs-item-main">
                              <span className="gs-item-title">
                                {fullName || e.email}
                              </span>
                              {e.jobTitle && (
                                <span className="gs-item-tag">
                                  {e.jobTitle}
                                </span>
                              )}
                            </div>
                            <div className="gs-item-meta">
                              {e.email && (
                                <span className="gs-item-meta-pill">
                                  {e.email}
                                </span>
                              )}
                              {e.group && (
                                <span className="gs-item-meta-pill">
                                  {e.group}
                                </span>
                              )}
                              {e.status && (
                                <span className="gs-item-meta-pill">
                                  {e.status}
                                </span>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {navResults.length > 0 && (
                <section className="gs-section">
                  <div className="gs-section-title">
                    <Search className="gs-section-icon" />
                    <span>Pages</span>
                  </div>
                  <ul className="gs-list">
                    {navResults.map((n) => {
                      const idx = flatResults.findIndex(
                        (x) => x.kind === "nav" && x.data.id === n.id
                      );
                      const selected = idx !== -1 && idx === selectedIndex;
                      return (
                        <li key={n.id}>
                          <button
                            type="button"
                            className={`gs-item ${selected ? "selected" : ""}`}
                            onClick={() =>
                              handleSelect({ kind: "nav", data: n })
                            }
                          >
                            <div className="gs-item-main">
                              <span className="gs-item-title">{n.label}</span>
                              {n.description && (
                                <span className="gs-item-sub">
                                  {n.description}
                                </span>
                              )}
                            </div>
                            <div className="gs-item-meta">
                              <span className="gs-item-meta-pill">
                                {n.type === "tab" ? "Dashboard" : "Settings"}
                              </span>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {helpResults.length > 0 && (
                <section className="gs-section">
                  <div className="gs-section-title">
                    <BookOpen className="gs-section-icon" />
                    <span>Help Center</span>
                  </div>
                  <ul className="gs-list">
                    {helpResults.map((h) => {
                      const idx = flatResults.findIndex(
                        (x) => x.kind === "help" && x.data.id === h.id
                      );
                      const selected = idx !== -1 && idx === selectedIndex;
                      return (
                        <li key={h.id}>
                          <button
                            type="button"
                            className={`gs-item ${selected ? "selected" : ""}`}
                            onClick={() =>
                              handleSelect({ kind: "help", data: h })
                            }
                          >
                            <div className="gs-item-main">
                              <span className="gs-item-title">{h.title}</span>
                              {h.summary && (
                                <span className="gs-item-sub">{h.summary}</span>
                              )}
                            </div>
                            <div className="gs-item-meta">
                              <span className="gs-item-meta-pill">
                                Help article
                              </span>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
